/**
 * Send a change-management template to a role/tenant audience.
 * Targeting is chosen at send time; preview is required before confirm.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Icons } from '../common/Icons';
import { emailTemplatesService, tenantsService } from '../../services/api';

const ROLE_OPTIONS = [
  { slug: 'super_admin', label: 'Super admin' },
  { slug: 'tenant_admin', label: 'Tenant admin' },
  { slug: 'admin', label: 'Admin' },
  { slug: 'accountant', label: 'Accountant' },
  { slug: 'payroll_admin', label: 'Payroll admin' },
  { slug: 'staff', label: 'Staff' },
  { slug: 'vendor', label: 'Vendor' },
  { slug: 'customer', label: 'Customer' },
  { slug: 'migration_operator', label: 'Migration operator' },
  { slug: 'migration_specialist', label: 'Migration specialist' },
];

const DEFAULT_ROLES = [
  'super_admin',
  'tenant_admin',
  'admin',
  'accountant',
  'payroll_admin',
];

const PREVIEW_CAP = 50;

const ChangeNoticeSendPanel = ({ templateId, disabled }) => {
  const [tenants, setTenants] = useState([]);
  const [roles, setRoles] = useState(() => new Set(DEFAULT_ROLES));
  const [selectedTenantIds, setSelectedTenantIds] = useState(() => new Set());
  const [excludeDemo, setExcludeDemo] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingTenants(true);
        const response = await tenantsService.getAll({ limit: 200 });
        if (!cancelled) setTenants(response.data || []);
      } catch (err) {
        console.error('Failed to load tenants:', err);
        if (!cancelled) setError('Failed to load workspaces');
      } finally {
        if (!cancelled) setLoadingTenants(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const audienceKey = useMemo(
    () => JSON.stringify({
      roles: [...roles].sort(),
      tenantIds: [...selectedTenantIds].sort(),
      excludeDemo,
      includeInactive,
    }),
    [roles, selectedTenantIds, excludeDemo, includeInactive]
  );

  useEffect(() => {
    setPreview(null);
    setSuccess('');
  }, [audienceKey, templateId]);

  const toggleRole = (slug) => {
    setRoles((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const toggleTenant = (id) => {
    setSelectedTenantIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const audienceBody = () => ({
    roles: [...roles],
    tenant_ids: [...selectedTenantIds],
    exclude_demo_tenants: excludeDemo,
    include_inactive_tenants: includeInactive,
  });

  const handlePreview = async () => {
    try {
      setBusy(true);
      setError(null);
      setSuccess('');
      const response = await emailTemplatesService.previewAudience(audienceBody());
      setPreview(response.data);
    } catch (err) {
      console.error('Failed to preview audience:', err);
      setPreview(null);
      setError(err.response?.data?.message || 'Failed to preview audience');
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    if (!preview || preview.count < 1) return;
    const ok = window.confirm(
      `Send this notice to ${preview.count} recipient${preview.count === 1 ? '' : 's'}? This cannot be undone.`
    );
    if (!ok) return;
    try {
      setBusy(true);
      setError(null);
      setSuccess('');
      const response = await emailTemplatesService.sendToAudience(templateId, {
        ...audienceBody(),
        confirm: true,
      });
      setSuccess(response.message || `Sent ${response.data?.sent || 0} email(s)`);
    } catch (err) {
      console.error('Failed to send change notice:', err);
      setError(err.response?.data?.message || 'Failed to send change notice');
    } finally {
      setBusy(false);
    }
  };

  const visibleRecipients = (preview?.recipients || []).slice(0, PREVIEW_CAP);
  const hiddenCount = Math.max(0, (preview?.count || 0) - visibleRecipients.length);

  return (
    <div className="card" style={{ marginTop: '24px' }}>
      <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Icons.Send size={18} />
        Send change notice
      </h3>
      <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
        Choose workspaces and roles at send time. Preview the list, then confirm.
        Leave workspaces unchecked to include every matching workspace.
        Fred&apos;s Farm is excluded by default.
      </p>

      {error && (
        <div className="alert alert-warning mb-3">
          <Icons.AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="alert alert-success mb-3">
          <Icons.CheckCircle size={18} />
          <span>{success}</span>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Roles</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
          {ROLE_OPTIONS.map((role) => (
            <label key={role.slug} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={roles.has(role.slug)}
                onChange={() => toggleRole(role.slug)}
                disabled={disabled || busy}
              />
              {role.label}
            </label>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Workspaces</label>
        {loadingTenants ? (
          <Icons.Loader size={18} className="spin" />
        ) : (
          <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 12px' }}>
            {tenants.map((tenant) => (
              <label
                key={tenant.id}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={selectedTenantIds.has(tenant.id)}
                  onChange={() => toggleTenant(tenant.id)}
                  disabled={disabled || busy}
                />
                <span>
                  {tenant.name || tenant.slug}
                  <span style={{ color: 'var(--text-muted)', marginLeft: '8px', fontSize: '12px' }}>
                    {tenant.slug}{tenant.is_active ? '' : ' · inactive'}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={excludeDemo}
            onChange={(e) => setExcludeDemo(e.target.checked)}
            disabled={disabled || busy}
          />
          Exclude demo workspace (freds-farm)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            disabled={disabled || busy}
          />
          Include inactive workspaces
        </label>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handlePreview}
          disabled={disabled || busy || roles.size === 0}
        >
          {busy && !preview ? 'Previewing...' : 'Preview recipients'}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSend}
          disabled={disabled || busy || !preview || preview.count < 1}
        >
          <Icons.Send size={16} />
          {busy ? 'Sending...' : preview ? `Send to ${preview.count}` : 'Send'}
        </button>
      </div>

      {preview && (
        <div>
          <p style={{ fontWeight: 600, marginBottom: '8px' }}>
            {preview.count} recipient{preview.count === 1 ? '' : 's'}
          </p>
          {preview.count === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>No matching accounts. Broaden roles or workspaces.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', fontSize: '13px' }}>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Workspace</th>
                    <th>Roles</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRecipients.map((row) => (
                    <tr key={`${row.tenant_id}:${row.account_id}`}>
                      <td>{row.email}</td>
                      <td>{row.name || '—'}</td>
                      <td>{row.tenant_name || row.tenant_slug}</td>
                      <td>{(row.roles || []).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hiddenCount > 0 && (
                <p style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                  And {hiddenCount} more not shown here.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChangeNoticeSendPanel;
